import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import useMailTemplate from '../../hooks/useMailTemplate';
import { Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
// simple TextField-based editing without WYSIWYG

function MailTemplateEditor() {
  const authCtx = useContext(AuthContext);
  if (!authCtx.checkRight('mailTemplate:update')) {
    return <Navigate to='/welcome' replace />;
  }

  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const navigate = useNavigate();
  const { getTemplateById, updateTemplate, getEmailWrappers } = useMailTemplate();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [htmlTemplate, setHtmlTemplate] = useState('');
  const [textTemplate, setTextTemplate] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getTemplateById(templateId);
      setTemplate(t);
      setHtmlContent(t.htmlContent || '');
      setTextContent(t.textContent || '');

      // load wrapper templates from assets via Vite raw import in hook
      const { htmlWrapper, textWrapper } = getEmailWrappers();
      setHtmlTemplate(htmlWrapper);
      setTextTemplate(textWrapper);
    } catch (e) {
      toast.error('Fehler beim Laden des Templates');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (templateId) fetchData();
  }, [templateId]);

  // plain text editors – no WYSIWYG

  const htmlPreview = useMemo(() => {
    if (!htmlTemplate) return '';
    return htmlTemplate.replace('{{content}}', htmlContent || '');
  }, [htmlTemplate, htmlContent]);

  const textPreview = useMemo(() => {
    if (!textTemplate) return '';
    return textTemplate.replace('{{content}}', textContent || '');
  }, [textTemplate, textContent]);

  const handleSave = useCallback(async () => {
    try {
      const updated = await updateTemplate(templateId, { htmlContent, textContent });
      setTemplate(updated);
      toast.success('Template gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  }, [templateId, htmlContent, textContent]);

  if (!templateId) return <Navigate to='/welcome' replace />;

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='h6'>Mail-Template bearbeiten</Typography>
        <Button variant='outlined' onClick={() => navigate(-1)}>
          Zurück
        </Button>
      </Box>
      {loading || !template ? (
        <Typography>Lade...</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='subtitle1'>HTML Inhalt</Typography>
              <TextField
                value={htmlContent}
                onChange={e => setHtmlContent(e.target.value)}
                multiline
                minRows={16}
                fullWidth
                placeholder='<p>HTML Inhalt...</p>'
              />
              <Typography variant='subtitle1'>Text Inhalt</Typography>
              <TextField
                value={textContent}
                onChange={e => setTextContent(e.target.value)}
                multiline
                minRows={10}
                fullWidth
                placeholder={'Text Inhalt...'}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant='contained' color='success' onClick={handleSave}>
                  Speichern
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='subtitle1'>HTML Vorschau</Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  title='htmlPreview'
                  style={{ width: '100%', height: 700, border: 0 }}
                  srcDoc={htmlPreview}
                />
              </Box>
              <Typography variant='subtitle1'>Text Vorschau</Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 160,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {textPreview}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default MailTemplateEditor;
